import React, { useCallback, useRef } from "react";
import type { TableColumnsType } from "antd";
import { ExpandableConfig, TableRowSelection } from "antd/es/table/interface";
import styled from "styled-components";
import InfiniteWrapper from "components/blocks/table/InfiniteWrapper";
import { CommonTable, Wrapper } from "components/atoms/table/AntdTable";

type Props = {
  columns: TableColumnsType<any>;
  data: any[];
  rowKey?: string | ((record: any) => React.Key);
  rowSelection?: TableRowSelection<any>;
  loading?: boolean;
  expandable?: ExpandableConfig<any>;
  rowClassName?: string;
  useInfiniteScroll: boolean;
  loadMore: () => void;
  hasMore: boolean;
  isInitialLoading: boolean;
  scrollHeight?: string;
};

export default function StagingAssignInfiniteTable({
  columns,
  data,
  rowKey,
  rowSelection,
  loading,
  expandable,
  rowClassName,
  useInfiniteScroll,
  loadMore,
  hasMore,
  isInitialLoading,
  scrollHeight = "calc(100dvh - 280px)",
}: Props): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);

  const getScrollContainer = useCallback((): HTMLElement | Window => {
    return (
      (containerRef.current?.querySelector(
        ".infinite-scroll"
      ) as HTMLElement | null) ?? window
    );
  }, []);

  const table = (
    <CommonTable
      columns={columns}
      dataSource={data}
      rowKey={rowKey}
      rowSelection={rowSelection}
      loading={loading}
      expandable={expandable}
      rowClassName={rowClassName}
      sticky={
        useInfiniteScroll
          ? { getContainer: getScrollContainer }
          : undefined
      }
    />
  );

  if (!useInfiniteScroll) {
    return table;
  }

  return (
    <AssignInfiniteTableWrapper ref={containerRef}>
      <InfiniteWrapper
        data={data}
        loadMore={loadMore}
        isLoading={isInitialLoading}
        hasMore={hasMore}
        height={scrollHeight}
      >
        {table}
      </InfiniteWrapper>
    </AssignInfiniteTableWrapper>
  );
}

const AssignInfiniteTableWrapper = styled(Wrapper)`
  .ant-table-sticky-holder {
    margin-right: var(--sbw, 0px);
    box-sizing: border-box;
    z-index: 3;
  }
`;
